/**
 * Per-workspace "recently visited pages" log, persisted to localStorage.
 *
 * Powers the empty-query state of the search palette — when the user opens
 * ⌘K we show their own visit history rather than a generic "recently
 * updated" list. The store is local-only by design: different devices show
 * different histories, matching the expected behavior.
 *
 * Storage contract:
 *   key   = `closot:recent-visits:v1`
 *   value = JSON of { [workspaceId]: VisitRecord[] }, newest first
 */
import type { SearchHit } from "@/services-frontend/search/searchClient";

const STORAGE_KEY = "closot:recent-visits:v1";
const MAX_PER_WORKSPACE = 30;

interface VisitRecord {
  hit: SearchHit;
  visitedAt: number;
}

type VisitStore = Record<string, VisitRecord[]>;

function read(): VisitStore {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as VisitStore) : {};
  } catch {
    return {};
  }
}

function write(store: VisitStore): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Quota or privacy mode — silently drop; visit history is non-critical.
  }
}

export interface VisitInput {
  id: string;
  title?: string;
  icon?: string;
  type?: SearchHit["type"];
  visibility?: SearchHit["visibility"];
  parentPageId?: string | null;
  workareaId?: string | null;
  /**
   * Ancestor path (root → immediate parent), excluding the item itself.
   * Stored so the "Recent" bucket can mirror the target: root pages show only
   * their icon + name, while nested pages / databases show their breadcrumb.
   * Empty (the default) means a root-level item.
   */
  breadcrumb?: SearchHit["breadcrumb"];
  updatedAt?: number;
  createdAt?: number;
}

/**
 * Coerces a partial input into the full SearchHit shape so we can render
 * visit history with the same component as search results. Missing fields
 * are filled with empty defaults — SearchResultItem renders them gracefully.
 */
function toHit(input: VisitInput): SearchHit {
  return {
    id: input.id,
    title: input.title || "New page",
    icon: input.icon || "",
    type: input.type ?? "page",
    visibility: input.visibility ?? null,
    parentId: "",
    parentPageId: input.parentPageId ?? null,
    workareaId: input.workareaId ?? null,
    owner: { id: "", name: "", email: "", image: "" },
    breadcrumb: input.breadcrumb ?? [],
    updatedAt: input.updatedAt ?? 0,
    createdAt: input.createdAt ?? 0,
  };
}

/**
 * Records a page visit. Dedupes by id (re-visiting moves the entry to the
 * top), and caps the per-workspace list at MAX_PER_WORKSPACE so the JSON
 * blob stays small.
 */
export function recordVisit(workspaceId: string, input: VisitInput): void {
  if (!workspaceId || !input.id) return;
  // Don't record content blocks — they aren't first-class navigable pages.
  if (input.type === "content") return;

  const store = read();
  const list = store[workspaceId] ?? [];
  const now = Date.now();

  const next: VisitRecord[] = [{ hit: toHit(input), visitedAt: now }];
  for (const v of list) {
    if (v.hit.id === input.id) continue;
    next.push(v);
    if (next.length >= MAX_PER_WORKSPACE) break;
  }

  store[workspaceId] = next;
  write(store);
}

export function getRecentVisits(workspaceId: string): SearchHit[] {
  if (!workspaceId) return [];
  const store = read();
  const list = store[workspaceId] ?? [];

  return list.map((v) =>
    v.hit.title === "Untitled" ? { ...v.hit, title: "New page" } : v.hit,
  );
}

export function clearRecentVisits(workspaceId?: string): void {
  if (!workspaceId) {
    write({});
    return;
  }
  const store = read();
  delete store[workspaceId];
  write(store);
}
